// ============================================
// STEP 6: REVIEW & PAYMENT
// ============================================

import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  CreditCard,
  CheckCircle,
  Shield,
  User,
  Laptop,
  Target,
  Settings,
  Monitor,
  MessageCircle,
  RotateCcw,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
  Button,
  Alert,
} from "../../../components/ui";
import { useFormStore, useAuthStore } from "../../../store";
import {
  createLead,
  createPaymentOrder,
  verifyPayment,
  getLeadById,
} from "../../../services";
import {
  SERVICE_FEE,
  EXPERT_YEARS,
  ROUTES,
  DEVICE_INTENT_OPTIONS,
  PERSONA_OPTIONS,
  PRIMARY_USAGE_OPTIONS,
  BUDGET_OPTIONS,
  DAILY_HOURS_OPTIONS,
  URGENCY_OPTIONS,
  OS_PREFERENCE_OPTIONS,
  PORTABILITY_OPTIONS,
  SCREEN_SIZE_OPTIONS,
  STORAGE_NEEDS_OPTIONS,
  BUDGET_FLEXIBILITY_OPTIONS,
  EXPECTED_USAGE_YEARS_OPTIONS,
  ERROR_MESSAGES,
} from "../../../constants";
import styles from "./Steps.module.css";

declare global {
  interface Window {
    Razorpay?: any;
  }
}

export function ReviewStep() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    formData,
    prevStep,
    setLeadId,
    clearLeadId,
    leadId,
    isSubmitting,
    setSubmitting,
    resetForm,
    clearAllData,
  } = useFormStore();
  const [error, setError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const {
    personalDetails,
    currentSetup,
    usageDetails,
    technicalPreferences,
    futurePlans,
  } = formData;

  // Get labels for display
  const getDeviceLabel = () =>
    DEVICE_INTENT_OPTIONS.find((o) => o.value === usageDetails.deviceIntent)
      ?.label || usageDetails.deviceIntent;

  const getPersonaLabel = () =>
    PERSONA_OPTIONS.find((o) => o.value === personalDetails.occupation)
      ?.label || personalDetails.occupation;

  const getUsageLabels = () =>
    usageDetails.primaryUsage.map(
      (u) => PRIMARY_USAGE_OPTIONS.find((o) => o.value === u)?.label || u,
    );

  const getBudgetLabel = () =>
    BUDGET_OPTIONS.find((o) => o.value === futurePlans.budgetRange)?.label ||
    futurePlans.budgetRange;

  const getDailyHoursLabel = () =>
    DAILY_HOURS_OPTIONS.find((o) => o.value === usageDetails.dailyHours)
      ?.label || usageDetails.dailyHours;

  const getUrgencyLabel = () =>
    URGENCY_OPTIONS.find((o) => o.value === futurePlans.urgency)?.label ||
    futurePlans.urgency;

  const getOsLabel = () =>
    OS_PREFERENCE_OPTIONS.find((o) => o.value === usageDetails.osPreference)
      ?.label || usageDetails.osPreference;

  const getPortabilityLabel = () =>
    PORTABILITY_OPTIONS.find((o) => o.value === usageDetails.needsPortability)
      ?.label || usageDetails.needsPortability;

  const getScreenLabel = () =>
    SCREEN_SIZE_OPTIONS.find(
      (o) => o.value === technicalPreferences.screenSizePreference,
    )?.label || technicalPreferences.screenSizePreference;

  const getStorageLabel = () =>
    STORAGE_NEEDS_OPTIONS.find(
      (o) => o.value === technicalPreferences.storageNeeds,
    )?.label || technicalPreferences.storageNeeds;

  const getBudgetFlexLabel = () =>
    BUDGET_FLEXIBILITY_OPTIONS.find(
      (o) => o.value === futurePlans.budgetFlexibility,
    )?.label || futurePlans.budgetFlexibility;

  const getExpectedYearsLabel = () =>
    EXPECTED_USAGE_YEARS_OPTIONS.find(
      (o) => o.value === futurePlans.expectedUsageYears,
    )?.label || futurePlans.expectedUsageYears;

  const getWhatsAppNumber = () =>
    personalDetails.whatsappSameAsPhone
      ? personalDetails.phone
      : personalDetails.whatsappNumber;

  const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if ((window as any).Razorpay) {
        resolve(true);
        return;
      }

      const existingScript = document.querySelector(
        'script[src*="checkout.razorpay.com"]',
      );
      if (existingScript) {
        let attempts = 0;
        const checkInterval = setInterval(() => {
          attempts++;
          if ((window as any).Razorpay) {
            clearInterval(checkInterval);
            resolve(true);
          } else if (attempts >= 50) {
            clearInterval(checkInterval);
            resolve(false);
          }
        }, 100);
        return;
      }

      const timeoutId = setTimeout(() => resolve(false), 10000);

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => {
        clearTimeout(timeoutId);
        resolve(true);
      };
      script.onerror = () => {
        clearTimeout(timeoutId);
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!termsAccepted) {
      setError("Please accept the terms and conditions to proceed");
      return;
    }

    if (!user) {
      setError("Session expired. Please login again.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      let currentLeadId = leadId;

      if (currentLeadId) {
        const existingLead = await getLeadById(currentLeadId);
        if (!existingLead) {
          clearLeadId();
          currentLeadId = null;
        }
      }

      if (!currentLeadId) {
        const lead = await createLead(user.id, formData);
        currentLeadId = lead.id;
        setLeadId(lead.id);
      }

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) throw new Error("Failed to load payment gateway.");

      if (
        typeof (window as any).Razorpay !== "function" &&
        typeof (window as any).Razorpay !== "object"
      ) {
        // allow both function/class or object shape; guard for safety
        // continue but will likely fail when trying to instantiate
      }

      const orderData = await createPaymentOrder(currentLeadId!);
      if (!orderData.order_id || !orderData.key_id)
        throw new Error("Failed to create payment order.");

      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "FL Smartech",
        description: "Device Recommendation - Expert Consultation",
        order_id: orderData.order_id,
        prefill: {
          email: personalDetails.email,
          contact: personalDetails.phone,
        },
        theme: { color: "#2563eb" },
        handler: async (response: any) => {
          try {
            const result = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              lead_id: currentLeadId!,
            });

            if (result.success) {
              resetForm();
              navigate(ROUTES.SUCCESS);
            } else {
              setSubmitting(false);
              navigate(ROUTES.FAILURE);
            }
          } catch (err) {
            setSubmitting(false);
            navigate(ROUTES.FAILURE);
          }
        },
        modal: { ondismiss: () => setSubmitting(false) },
      } as any;

      try {
        const razorpay = new (window as any).Razorpay(options);
        razorpay.open();
      } catch (razorpayError) {
        throw new Error("Failed to open payment window.");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : ERROR_MESSAGES.PAYMENT_FAILED;
      setError(errorMessage);
      setSubmitting(false);
    }
  };

  return (
    <>
      {isSubmitting && (
        <div className={styles.paymentLoaderOverlay}>
          <div className={styles.paymentLoader}>
            <Loader2
              size={48}
              className={styles.spinner}
              style={{ color: "#2563eb" }}
            />
            <div className={styles.paymentLoaderText}>
              Processing payment...
              <br />
              Please do not close or refresh this page.
            </div>
          </div>
        </div>
      )}
      {/* <Toast message={error} onClose={() => setError('')} /> */}
      <Card variant="elevated">
        <CardHeader>
          <CardTitle>Review your information</CardTitle>
          <CardDescription>
            Please verify your details before proceeding to payment.
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className={styles.reviewContainer}>
            {error && <Alert variant="error">{error}</Alert>}

            {/* Personal Details Summary */}
            <div className={styles.reviewSection}>
              <div className={styles.reviewHeader}>
                <User size={18} />
                <h3>Personal Details</h3>
              </div>
              <div className={styles.reviewGrid}>
                <div className={styles.reviewItem}>
                  <span className={styles.reviewLabel}>Name</span>
                  <span className={styles.reviewValue}>
                    {personalDetails.name}
                  </span>
                </div>
                <div className={styles.reviewItem}>
                  <span className={styles.reviewLabel}>Profile</span>
                  <span className={styles.reviewValue}>
                    {getPersonaLabel()}
                  </span>
                </div>
                <div className={styles.reviewItem}>
                  <span className={styles.reviewLabel}>Email</span>
                  <span className={styles.reviewValue}>
                    {personalDetails.email}
                  </span>
                </div>
                <div className={styles.reviewItem}>
                  <span className={styles.reviewLabel}>Phone</span>
                  <span className={styles.reviewValue}>
                    {personalDetails.phone}
                  </span>
                </div>
                <div className={styles.reviewItem}>
                  <span className={styles.reviewLabel}>
                    <MessageCircle size={14} style={{ marginRight: 4 }} />
                    WhatsApp
                  </span>
                  <span className={styles.reviewValue}>
                    {getWhatsAppNumber()}
                  </span>
                </div>
                <div className={styles.reviewItem}>
                  <span className={styles.reviewLabel}>Location</span>
                  <span className={styles.reviewValue}>
                    {personalDetails.location}
                  </span>
                </div>
              </div>
            </div>

            {/* Current Setup Summary */}
            {currentSetup.hasCurrentDevice && (
              <div className={styles.reviewSection}>
                <div className={styles.reviewHeader}>
                  <Monitor size={18} />
                  <h3>Current Setup</h3>
                </div>
                <div className={styles.reviewGrid}>
                  <div className={styles.reviewItem}>
                    <span className={styles.reviewLabel}>Current Device</span>
                    <span className={styles.reviewValue}>
                      {currentSetup.currentDeviceBrand}{" "}
                      {currentSetup.currentDeviceType}
                    </span>
                  </div>
                  <div className={styles.reviewItem}>
                    <span className={styles.reviewLabel}>Age</span>
                    <span className={styles.reviewValue}>
                      {currentSetup.currentDeviceAge?.replace(/_/g, " ")}
                    </span>
                  </div>
                  {currentSetup.painPoints &&
                    currentSetup.painPoints.length > 0 && (
                      <div className={styles.reviewItemFull}>
                        <span className={styles.reviewLabel}>Pain Points</span>
                        <span className={styles.reviewValue}>
                          {currentSetup.painPoints.join(", ")}
                        </span>
                      </div>
                    )}
                </div>
              </div>
            )}

            {/* Usage Details Summary */}
            <div className={styles.reviewSection}>
              <div className={styles.reviewHeader}>
                <Laptop size={18} />
                <h3>Usage Details</h3>
              </div>
              <div className={styles.reviewGrid}>
                <div className={styles.reviewItem}>
                  <span className={styles.reviewLabel}>Looking for</span>
                  <span className={styles.reviewValue}>{getDeviceLabel()}</span>
                </div>
                <div className={styles.reviewItem}>
                  <span className={styles.reviewLabel}>Daily Usage</span>
                  <span className={styles.reviewValue}>
                    {getDailyHoursLabel()}
                  </span>
                </div>
                <div className={styles.reviewItem}>
                  <span className={styles.reviewLabel}>Portability</span>
                  <span className={styles.reviewValue}>
                    {getPortabilityLabel()}
                  </span>
                </div>
                <div className={styles.reviewItem}>
                  <span className={styles.reviewLabel}>OS Preference</span>
                  <span className={styles.reviewValue}>{getOsLabel()}</span>
                </div>
                <div className={styles.reviewItemFull}>
                  <span className={styles.reviewLabel}>Primary Uses</span>
                  <span className={styles.reviewValue}>
                    {getUsageLabels().join(", ")}
                  </span>
                </div>
                {usageDetails.specificSoftware && (
                  <div className={styles.reviewItemFull}>
                    <span className={styles.reviewLabel}>
                      Specific Software
                    </span>
                    <span className={styles.reviewValue}>
                      {usageDetails.specificSoftware}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Technical Preferences Summary */}
            <div className={styles.reviewSection}>
              <div className={styles.reviewHeader}>
                <Settings size={18} />
                <h3>Technical Preferences</h3>
              </div>
              <div className={styles.reviewGrid}>
                <div className={styles.reviewItem}>
                  <span className={styles.reviewLabel}>Screen Size</span>
                  <span className={styles.reviewValue}>{getScreenLabel()}</span>
                </div>
                <div className={styles.reviewItem}>
                  <span className={styles.reviewLabel}>Storage</span>
                  <span className={styles.reviewValue}>
                    {getStorageLabel()}
                  </span>
                </div>
                {technicalPreferences.brandPreference &&
                  technicalPreferences.brandPreference.length > 0 && (
                    <div className={styles.reviewItem}>
                      <span className={styles.reviewLabel}>
                        Preferred Brands
                      </span>
                      <span className={styles.reviewValue}>
                        {technicalPreferences.brandPreference.join(", ")}
                      </span>
                    </div>
                  )}
                {technicalPreferences.priorityFeatures &&
                  technicalPreferences.priorityFeatures.length > 0 && (
                    <div className={styles.reviewItemFull}>
                      <span className={styles.reviewLabel}>
                        Priority Features
                      </span>
                      <span className={styles.reviewValue}>
                        {technicalPreferences.priorityFeatures.join(", ")}
                      </span>
                    </div>
                  )}
              </div>
            </div>

            {/* Future Plans Summary */}
            <div className={styles.reviewSection}>
              <div className={styles.reviewHeader}>
                <Target size={18} />
                <h3>Budget & Plans</h3>
              </div>
              <div className={styles.reviewGrid}>
                <div className={styles.reviewItem}>
                  <span className={styles.reviewLabel}>Budget</span>
                  <span className={styles.reviewValue}>{getBudgetLabel()}</span>
                </div>
                <div className={styles.reviewItem}>
                  <span className={styles.reviewLabel}>Flexibility</span>
                  <span className={styles.reviewValue}>
                    {getBudgetFlexLabel()}
                  </span>
                </div>
                <div className={styles.reviewItem}>
                  <span className={styles.reviewLabel}>Timeline</span>
                  <span className={styles.reviewValue}>
                    {getUrgencyLabel()}
                  </span>
                </div>
                <div className={styles.reviewItem}>
                  <span className={styles.reviewLabel}>Expected Use</span>
                  <span className={styles.reviewValue}>
                    {getExpectedYearsLabel()}
                  </span>
                </div>
                {futurePlans.plannedActivities.length > 0 && (
                  <div className={styles.reviewItemFull}>
                    <span className={styles.reviewLabel}>Future Plans</span>
                    <span className={styles.reviewValue}>
                      {futurePlans.plannedActivities.join(", ")}
                    </span>
                  </div>
                )}
                {futurePlans.additionalNotes && (
                  <div className={styles.reviewItemFull}>
                    <span className={styles.reviewLabel}>Notes</span>
                    <span className={styles.reviewValue}>
                      {futurePlans.additionalNotes}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Info */}
            <div className={styles.paymentBox}>
              <div className={styles.paymentDetails}>
                <span className={styles.paymentLabel}>
                  Expert Consultation Fee
                </span>
                <span className={styles.paymentAmount}>₹{SERVICE_FEE}</span>
              </div>
              <p className={styles.paymentNote}>
                {EXPERT_YEARS}+ years industry expertise • Personalized
                recommendation via WhatsApp
              </p>
            </div>

            {/* Terms */}
            <label className={styles.termsLabel}>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className={styles.checkbox}
              />
              <span>
                I agree to the{" "}
                <Link to={ROUTES.TERMS} target="_blank">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to={ROUTES.PRIVACY} target="_blank">
                  Privacy Policy
                </Link>
              </span>
            </label>

            {/* Trust Badges */}
            <div className={styles.trustBadges}>
              <div className={styles.trustBadge}>
                <Shield size={16} />
                <span>Secure Payment</span>
              </div>
              <div className={styles.trustBadge}>
                <CheckCircle size={16} />
                <span>Data Protected</span>
              </div>
            </div>
          </div>
        </CardContent>

        <CardFooter 
            
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              padding: '16px',
              width: '100%',
              alignItems: 'stretch'
            }}
          >
          <Button
            onClick={handlePayment}
            size="lg"
            isLoading={isSubmitting}
            disabled={!termsAccepted || isSubmitting}
            className={styles.primaryAction}
          >
            <CreditCard size={18} />
            Pay ₹{SERVICE_FEE}
          </Button>
          <div className={styles.secondaryActions}>
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={isSubmitting}
              className={styles.flexBtn}
            >
              <ArrowLeft size={18} /> Back
            </Button>

            <Button
              variant="ghost"
              onClick={() => {
                if (confirm("Start over?")) clearAllData();
              }}
              disabled={isSubmitting}
              className={styles.flexBtn}
            >
              <RotateCcw size={16} /> Reset
            </Button>
          </div>


        </CardFooter>
      </Card>
    </>
  );
}
